import type { ComponentProps } from 'react';
import { StyleSheet } from 'react-native-unistyles';
import { getRelativeTimeInfo } from '@/components/Event/EventList.utils';
import { EventListItemFragment } from '@/components/Event/EventListItem.types';
import { type FragmentOf, readFragment } from '@/gql/graphql';
import Box from '../UI/Box';
import Item from '../UI/PressableRow';
import EventListItemInfo from './EventListItemInfo';
import EventListItemTeams from './EventListItemTeams';
import EventListItemTime from './EventListItemTime';
import EventRsvpSummary from './EventRsvpSummary';

export const Info = EventListItemInfo;
export const Teams = EventListItemTeams;
export const Time = EventListItemTime;

export function Header({ children }: { children: React.ReactNode }) {
  return (
    <Box style={style.header} space="lg" alignItems="center">
      {children}
    </Box>
  );
}

export function Content({ children }: { children: React.ReactNode }) {
  return (
    <Box direction="horizontal" style={style.content}>
      {children}
    </Box>
  );
}

function Pressable({
  relativeTimeInfo,
  ...props
}: {
  relativeTimeInfo: ReturnType<typeof getRelativeTimeInfo>;
} & ComponentProps<typeof Item>) {
  return (
    <Item
      {...props}
      style={style.item(relativeTimeInfo)}
      testID="EventListItem"
    >
      {props.children}
    </Item>
  );
}

export function Container({
  children,
  relativeTimeInfo,
}: {
  children: React.ReactNode;
  relativeTimeInfo: ReturnType<typeof getRelativeTimeInfo>;
}) {
  return (
    <Box style={style.item(relativeTimeInfo)} testID="EventListItem">
      {children}
    </Box>
  );
}

type EventListItemProps = {
  fragment: FragmentOf<typeof EventListItemFragment> | null | undefined;
  nowMs?: number;
  isNextUpcomingEvent?: boolean;
} & Pick<ComponentProps<typeof Item>, 'onPress'>;

function EventListItem({
  fragment,
  nowMs,
  isNextUpcomingEvent,
  ...props
}: EventListItemProps) {
  const event = readFragment(EventListItemFragment, fragment);

  const relativeTimeInfo = getRelativeTimeInfo({
    status: event?.status,
    hasClock: !!event?.event_clock?.event_id,
    datetime: event?.datetime,
    nowMs,
    isNextUpcomingEvent,
  });

  return (
    <Pressable relativeTimeInfo={relativeTimeInfo} {...props}>
      <Header>
        <Time fragment={event} relativeTimeInfo={relativeTimeInfo} />
        <Info fragment={event} relativeTimeInfo={relativeTimeInfo} />
      </Header>
      <Content>
        <Teams fragment={event} rsvpFragment={event} />
        <EventRsvpSummary fragment={event} size="lg" />
      </Content>
    </Pressable>
  );
}

export default EventListItem;

const style = StyleSheet.create((theme) => ({
  content: {
    flex: 1,
    alignItems: 'center',
  },
  item: (relativeTimeInfo: ReturnType<typeof getRelativeTimeInfo>) => ({
    marginHorizontal: theme.size(2),
    marginVertical: theme.size(1),
    flexDirection: 'column',
    alignItems: 'stretch',
    borderRadius: theme.borderRadius.xl,
    overflow: 'hidden',
    backgroundColor: theme.colors.surface,
    paddingHorizontal: 0,
    paddingVertical: 0,
    gap: 0,
    borderWidth: theme.size(0.25),
    borderColor:
      relativeTimeInfo?.variant === 'info'
        ? theme.colors.infoMuted
        : relativeTimeInfo?.variant === 'warning'
          ? theme.colors.warningMuted
          : theme.colors.borderMuted,
  }),
  header: {
    flexDirection: 'row',
    alignItems: 'flex-start',
  },
}));
